import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import swal from 'sweetalert';
import Sidebar from './Sidebar';
import MetaTag from '../layout/MetaTag';
import Loader from '../layout/loader';
import { getCategoryIcon } from '../components/marketplace/CategoryIcons';
import './taxonomyExplorer.css';

// Material-UI Icons
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CallMergeOutlinedIcon from '@mui/icons-material/CallMergeOutlined';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select
} from '@material-ui/core';

function TaxonomyExplorer() {
  const history = useHistory();

  // Primary States
  const [activeTab, setActiveTab] = useState('tree'); // 'tree' | 'requests'
  const [tree, setTree] = useState([]);
  const [stats, setStats] = useState({
    totalCategories: 0,
    totalSubcategories: 0,
    totalProducts: 0,
    pendingRequests: 0,
  });
  const [health, setHealth] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive' | 'empty'
  const [expandedNodes, setExpandedNodes] = useState({});

  // Slide-Over Detail Drawer State
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Node Actions Menu State
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTargetNode, setMenuTargetNode] = useState(null);

  // Category Modal State (Add / Edit)
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSubcategoryModalOpen, setIsSubcategoryModalOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [formParentId, setFormParentId] = useState('');
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formStatus, setFormStatus] = useState('active');
  const [formIsFeatured, setFormIsFeatured] = useState(false);
  const [formDisplayOrder, setFormDisplayOrder] = useState(1);
  const [formTags, setFormTags] = useState([]);
  const [similarWarnings, setSimilarWarnings] = useState([]);

  // Merge Modal State
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergeSourceNode, setMergeSourceNode] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState('');

  // Seller Request Review Modal
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [reviewAction, setReviewAction] = useState('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAssignParentId, setReviewAssignParentId] = useState('');

  // 1. Fetch All Data
  const loadTaxonomyData = useCallback(async () => {
    try {
      setLoading(true);
      const [treeRes, healthRes, requestsRes] = await Promise.all([
        axios.get('/api/admin/taxonomy/tree?status=all'),
        axios.get('/api/admin/taxonomy/health'),
        axios.get('/api/admin/category-requests?status=all')
      ]);

      const t = treeRes.data.tree || [];
      setTree(t);
      setStats({
        totalCategories: treeRes.data.totalCategories || 0,
        totalSubcategories: treeRes.data.totalSubcategories || 0,
        totalProducts: treeRes.data.totalProducts || 0,
        pendingRequests: treeRes.data.pendingRequests || 0,
      });
      setHealth(healthRes.data.health || null);
      setRequests(requestsRes.data.requests || []);

      // Auto expand top 4 categories initially
      const initialExpanded = {};
      t.slice(0, 4).forEach(node => {
        initialExpanded[node._id] = true;
      });
      setExpandedNodes(prev => ({ ...initialExpanded, ...prev }));
    } catch (err) {
      console.error("Failed to fetch taxonomy data:", err);
      swal("Error", "Could not load taxonomy management data.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTaxonomyData();
  }, [loadTaxonomyData]);

  // 2. Fetch Detail Drawer data when node is selected
  useEffect(() => {
    if (!selectedNodeId) {
      setDrawerData(null);
      return;
    }
    const fetchDrawer = async () => {
      try {
        setDrawerLoading(true);
        const res = await axios.get(`/api/admin/taxonomy/drawer/${selectedNodeId}`);
        if (res.data && res.data.success) {
          setDrawerData(res.data);
        }
      } catch (err) {
        console.error("Failed to load category drawer details:", err);
      } finally {
        setDrawerLoading(false);
      }
    };
    fetchDrawer();
  }, [selectedNodeId]);

  // 3. Live Duplicate / Similarity Check
  useEffect(() => {
    if (!formName || formName.trim().length < 3) {
      setSimilarWarnings([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(
          `/api/admin/taxonomy/check-similarity?name=${encodeURIComponent(formName)}&parentId=${formParentId || 'null'}&currentId=${editingNode ? editingNode._id : ''}`
        );
        if (res.data?.similarMatches?.length > 0) {
          setSimilarWarnings(res.data.similarMatches);
        } else {
          setSimilarWarnings([]);
        }
      } catch {
        setSimilarWarnings([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [formName, formParentId, editingNode]);

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const handleExpandAll = () => {
    const all = {};
    tree.forEach(cat => {
      all[cat._id] = true;
    });
    setExpandedNodes(all);
  };

  const handleCollapseAll = () => {
    setExpandedNodes({});
  };

  // Filtered Tree based on Search & Status
  const filteredTree = useMemo(() => {
    let list = [...tree];
    const q = searchQuery.toLowerCase().trim();

    if (q) {
      list = list.filter(cat => {
        const catMatch = cat.name.toLowerCase().includes(q) || cat.slug?.toLowerCase().includes(q);
        const subMatch = (cat.children || []).some(sub => sub.name.toLowerCase().includes(q) || sub.slug?.toLowerCase().includes(q));
        return catMatch || subMatch;
      });
    }

    if (statusFilter === 'active') {
      list = list.filter(cat => cat.status === 'active');
    } else if (statusFilter === 'inactive') {
      list = list.filter(cat => cat.status === 'inactive' || cat.status === 'archived');
    } else if (statusFilter === 'empty') {
      list = list.filter(cat => (cat.productCount || 0) === 0);
    }

    return list;
  }, [tree, searchQuery, statusFilter]);

  // Node Actions Handlers
  const handleOpenActionMenu = (e, node) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuTargetNode(node);
  };

  const handleCloseActionMenu = () => {
    setMenuAnchorEl(null);
    setMenuTargetNode(null);
  };

  const handleOpenAddCategory = () => {
    setEditingNode(null);
    setFormParentId('');
    setFormName('');
    setFormSlug('');
    setFormDesc('');
    setFormStatus('active');
    setFormIsFeatured(false);
    setFormDisplayOrder(stats.totalCategories + 1);
    setFormTags([]);
    setSimilarWarnings([]);
    setIsCategoryModalOpen(true);
  };

  const handleOpenAddSubcategory = (parentCategory = null) => {
    setEditingNode(null);
    setFormParentId(parentCategory ? parentCategory._id : tree[0]?._id || '');
    setFormName('');
    setFormSlug('');
    setFormDesc('');
    setFormStatus('active');
    setFormDisplayOrder(1);
    setFormTags([]);
    setSimilarWarnings([]);
    setIsSubcategoryModalOpen(true);
  };

  const handleOpenEdit = (node) => {
    setEditingNode(node);
    setFormParentId(node.parentId?._id || node.parentId || '');
    setFormName(node.name || '');
    setFormSlug(node.slug || '');
    setFormDesc(node.description || node.desc || '');
    setFormStatus(node.status || 'active');
    setFormIsFeatured(!!node.isFeatured);
    setFormDisplayOrder(node.displayOrder || 1);
    setFormTags(node.tags || []);
    setSimilarWarnings([]);

    if (node.level === 2 || node.parentId) {
      setIsSubcategoryModalOpen(true);
    } else {
      setIsCategoryModalOpen(true);
    }
    handleCloseActionMenu();
  };

  const handleOpenMerge = (node) => {
    setMergeSourceNode(node);
    setMergeTargetId('');
    setIsMergeModalOpen(true);
    handleCloseActionMenu();
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      return swal("Validation", "Category name is required.", "warning");
    }

    try {
      const payload = {
        name: formName.trim(),
        slug: formSlug.trim() || undefined,
        description: formDesc.trim(),
        status: formStatus,
        isFeatured: formIsFeatured,
        displayOrder: Number(formDisplayOrder),
        level: 1,
        type: 'CATEGORY',
        tags: formTags
      };

      if (editingNode) {
        await axios.put(`/api/admin/categories/${editingNode._id}`, payload);
        swal("Success", `Category "${payload.name}" updated.`, "success");
      } else {
        await axios.post('/api/admin/categories', payload);
        swal("Success", `Category "${payload.name}" created.`, "success");
      }

      setIsCategoryModalOpen(false);
      loadTaxonomyData();
    } catch (err) {
      swal("Error", err.response?.data?.msg || "Failed to save category.", "error");
    }
  };

  const handleSaveSubcategory = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      return swal("Validation", "Subcategory name is required.", "warning");
    }
    if (!formParentId) {
      return swal("Validation", "Please select a parent category.", "warning");
    }

    try {
      const payload = {
        name: formName.trim(),
        slug: formSlug.trim() || undefined,
        parentId: formParentId,
        description: formDesc.trim(),
        status: formStatus,
        displayOrder: Number(formDisplayOrder),
        level: 2,
        type: 'SUBCATEGORY',
        tags: formTags
      };

      if (editingNode) {
        await axios.put(`/api/admin/categories/${editingNode._id}`, payload);
        swal("Success", `Subcategory "${payload.name}" updated.`, "success");
      } else {
        await axios.post('/api/admin/categories', payload);
        swal("Success", `Subcategory "${payload.name}" created.`, "success");
      }

      setIsSubcategoryModalOpen(false);
      loadTaxonomyData();
    } catch (err) {
      swal("Error", err.response?.data?.msg || "Failed to save subcategory.", "error");
    }
  };

  const handleExecuteMerge = async () => {
    if (!mergeSourceNode || !mergeTargetId) {
      return swal("Validation", "Please select a target category to merge into.", "warning");
    }

    try {
      const res = await axios.post('/api/admin/categories/merge', {
        sourceCategoryId: mergeSourceNode._id,
        targetCategoryId: mergeTargetId
      });

      swal("Merged Successfully", res.data.message || "Categories merged.", "success");
      setIsMergeModalOpen(false);
      loadTaxonomyData();
      if (selectedNodeId === mergeSourceNode._id) {
        setSelectedNodeId(mergeTargetId);
      }
    } catch (err) {
      swal("Merge Failed", err.response?.data?.msg || "Failed to execute merge.", "error");
    }
  };

  const handleDeleteNode = async (node) => {
    handleCloseActionMenu();
    const willDelete = await swal({
      title: `Delete / Archive "${node.name}"?`,
      text: `If products belong to this category, it will be safely archived without data loss. If 0 products exist, it will be permanently deleted.`,
      icon: "warning",
      buttons: ["Cancel", "Confirm Delete"],
      dangerMode: true,
    });

    if (willDelete) {
      try {
        const res = await axios.delete(`/api/admin/categories/${node._id}`);
        swal("Removed", res.data.message || "Category removed.", "success");
        loadTaxonomyData();
        if (selectedNodeId === node._id) setSelectedNodeId(null);
      } catch (err) {
        swal("Error", err.response?.data?.msg || "Failed to remove category.", "error");
      }
    }
  };

  const handleToggleStatus = async (node) => {
    handleCloseActionMenu();
    const newStatus = node.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`/api/admin/categories/${node._id}`, { status: newStatus });
      swal("Status Updated", `"${node.name}" is now ${newStatus}.`, "success");
      loadTaxonomyData();
    } catch (err) {
      swal("Error", "Failed to update category status.", "error");
    }
  };

  // Seller Request Review
  const handleOpenReviewRequest = (req, action = 'approve') => {
    setReviewingRequest(req);
    setReviewAction(action);
    setReviewNotes(req.adminNotes || '');
    setReviewAssignParentId(tree[0]?._id || '');
  };

  const handleExecuteRequestReview = async () => {
    if (!reviewingRequest) return;
    try {
      await axios.put(`/api/admin/category-requests/${reviewingRequest._id}`, {
        action: reviewAction,
        adminNotes: reviewNotes,
        createAsNew: reviewAction === 'approve',
        assignCategoryId: reviewAssignParentId || undefined
      });

      swal("Request Updated", `Seller request marked as ${reviewAction}.`, "success");
      setReviewingRequest(null);
      loadTaxonomyData();
    } catch (err) {
      swal("Error", err.response?.data?.msg || "Failed to review request.", "error");
    }
  };

  const handleExportTaxonomy = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(tree, null, 2));
    const dlAnchorElem = document.createElement('a');
    dlAnchorElem.setAttribute("href", dataStr);
    dlAnchorElem.setAttribute("download", `imshop_taxonomy_export_${new Date().toISOString().slice(0, 10)}.json`);
    dlAnchorElem.click();
  };

  return (
    <div className="dashboard-container">
      <MetaTag
        title="Taxonomy Explorer | IMSHOP Admin"
        description="Marketplace central taxonomy management interface."
      />
      <Sidebar />

      <main className="main-container admin-dashboard-main">
        {/* ═════════════════ 1. HEADER ═════════════════ */}
        <div className="im-tax-header">
          <div className="im-tax-header-left">
            <span className="im-tax-eyebrow">TAXONOMY MANAGEMENT</span>
            <h1 className="im-tax-title">Taxonomy Explorer</h1>
            <p className="im-tax-desc">
              Central management for marketplace categories, subcategories, live product metrics, and seller classification requests.
            </p>
          </div>

          <div className="im-tax-header-actions">
            <button
              type="button"
              className="im-btn-secondary"
              onClick={handleExportTaxonomy}>
              <FileDownloadOutlinedIcon fontSize="small" />
              <span>Export Taxonomy</span>
            </button>
            <button
              type="button"
              className="im-btn-secondary"
              onClick={() => handleOpenAddSubcategory()}>
              <AddCircleOutlineIcon fontSize="small" />
              <span>+ Add Subcategory</span>
            </button>
            <button
              type="button"
              className="im-btn-primary"
              onClick={handleOpenAddCategory}>
              <AddCircleOutlineIcon fontSize="small" />
              <span>+ Add Category</span>
            </button>
          </div>
        </div>

        {/* ═════════════════ 2. LIVE SUMMARY KPI BAR ═════════════════ */}
        <div className="im-tax-stats-grid">
          <div className="im-tax-stat-card">
            <div className="im-tax-stat-icon" style={{ background: '#fdf6f5', color: '#8b2626' }}>
              <CategoryOutlinedIcon />
            </div>
            <div className="im-tax-stat-meta">
              <span className="im-tax-stat-label">Total Categories</span>
              <span className="im-tax-stat-val">{stats.totalCategories}</span>
            </div>
          </div>

          <div className="im-tax-stat-card">
            <div className="im-tax-stat-icon" style={{ background: '#eff6ff', color: '#1d4ed8' }}>
              <AccountTreeOutlinedIcon />
            </div>
            <div className="im-tax-stat-meta">
              <span className="im-tax-stat-label">Total Subcategories</span>
              <span className="im-tax-stat-val">{stats.totalSubcategories}</span>
            </div>
          </div>

          <div className="im-tax-stat-card">
            <div className="im-tax-stat-icon" style={{ background: '#f0fdf4', color: '#16a34a' }}>
              <Inventory2OutlinedIcon />
            </div>
            <div className="im-tax-stat-meta">
              <span className="im-tax-stat-label">Products Classified</span>
              <span className="im-tax-stat-val">{stats.totalProducts.toLocaleString()}</span>
            </div>
          </div>

          <div className="im-tax-stat-card">
            <div className="im-tax-stat-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
              <StorefrontOutlinedIcon />
            </div>
            <div className="im-tax-stat-meta">
              <span className="im-tax-stat-label">Pending Requests</span>
              <span className="im-tax-stat-val">{stats.pendingRequests}</span>
            </div>
          </div>
        </div>

        {/* ═════════════════ 3. TAXONOMY HEALTH SECTION ═════════════════ */}
        {health && (
          <div className="im-tax-health-box">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <WarningAmberOutlinedIcon style={{ color: '#8b2626' }} />
                <h3 style={{ margin: 0, fontSize: '14.5px', fontWeight: '800', color: '#111315' }}>
                  Taxonomy Health & Hygiene
                </h3>
              </div>
              <div className="im-tax-health-items">
                {health.otherProductsCount > 0 && (
                  <span className="im-health-pill im-health-pill--warning">
                    ⚠ {health.otherProductsCount} products classified as "Other"
                  </span>
                )}
                {health.pendingRequestsCount > 0 && (
                  <span className="im-health-pill im-health-pill--warning">
                    ⚠ {health.pendingRequestsCount} pending seller taxonomy requests
                  </span>
                )}
                {health.emptyCategoriesCount > 0 ? (
                  <span className="im-health-pill im-health-pill--info">
                    ℹ {health.emptyCategoriesCount} primary categories have 0 products
                  </span>
                ) : (
                  <span className="im-health-pill im-health-pill--success">
                    ✓ All primary categories active
                  </span>
                )}
                {health.duplicateClustersCount > 0 && (
                  <span className="im-health-pill im-health-pill--warning">
                    ⚠ {health.duplicateClustersCount} potentially similar category pairs detected
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={loadTaxonomyData}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12.5px', fontWeight: '700' }}>
              <RefreshIcon fontSize="small" />
              <span>Refresh</span>
            </button>
          </div>
        )}

        {/* ═════════════════ 4. TABS: TREE vs SELLER REQUESTS ═════════════════ */}
        <div className="im-tax-tabs-row">
          <button
            type="button"
            className={`im-tax-tab-btn ${activeTab === 'tree' ? 'im-tax-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('tree')}>
            <AccountTreeOutlinedIcon fontSize="small" />
            <span>Taxonomy Tree</span>
          </button>
          <button
            type="button"
            className={`im-tax-tab-btn ${activeTab === 'requests' ? 'im-tax-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('requests')}>
            <StorefrontOutlinedIcon fontSize="small" />
            <span>Seller Requests</span>
            {stats.pendingRequests > 0 && (
              <span className="im-tax-tab-badge">{stats.pendingRequests}</span>
            )}
          </button>
        </div>

        {/* ═════════════════ TAB 1: TAXONOMY TREE ═════════════════ */}
        {activeTab === 'tree' && (
          <div>
            {/* Search & Filter Toolbar */}
            <div className="im-tax-toolbar">
              <div className="im-tax-search-wrap">
                <SearchIcon style={{ position: 'absolute', left: '12px', top: '10px', color: '#9ca3af' }} />
                <input
                  type="text"
                  className="im-tax-search-input"
                  placeholder="Search categories, subcategories or products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="im-tax-filter-pills">
                {['all', 'active', 'inactive', 'empty'].map(s => (
                  <button
                    key={s}
                    type="button"
                    className={`im-tax-pill-btn ${statusFilter === s ? 'im-tax-pill-btn--active' : ''}`}
                    onClick={() => setStatusFilter(s)}>
                    {s === 'all' ? 'All' : s === 'active' ? 'Active' : s === 'inactive' ? 'Inactive / Archived' : 'Empty (0)'}
                  </button>
                ))}

                <button
                  type="button"
                  className="im-tax-pill-btn"
                  onClick={handleExpandAll}>
                  Expand All
                </button>
                <button
                  type="button"
                  className="im-tax-pill-btn"
                  onClick={handleCollapseAll}>
                  Collapse All
                </button>
              </div>
            </div>

            {/* Tree Component */}
            {loading ? (
              <Loader />
            ) : filteredTree.length > 0 ? (
              <div className="im-tax-tree-card">
                {filteredTree.map(catNode => {
                  const isExpanded = !!expandedNodes[catNode._id];
                  const hasChildren = catNode.children && catNode.children.length > 0;
                  const isSelected = selectedNodeId === catNode._id;

                  return (
                    <div key={catNode._id}>
                      {/* Category Row */}
                      <div
                        className={`im-tree-node-row ${isSelected ? 'im-tree-node-row--selected' : ''}`}
                        onClick={() => setSelectedNodeId(catNode._id)}>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleNode(catNode._id);
                          }}
                          style={{ cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
                          {hasChildren ? (
                            isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />
                          ) : (
                            <span style={{ width: '20px', display: 'inline-block' }} />
                          )}
                        </div>

                        <div className="im-tree-node-icon">
                          {getCategoryIcon(catNode, 18)}
                        </div>

                        <div className="im-tree-node-title-box">
                          <span className="im-tree-node-name">{catNode.name}</span>
                          <span className="im-tree-node-slug">/{catNode.slug}</span>
                          {catNode.name === 'Other' && (
                            <span style={{ fontSize: '10.5px', background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: '4px', fontWeight: '800' }}>
                              Fallback
                            </span>
                          )}
                        </div>

                        <div className="im-tree-node-badges">
                          <span className="im-count-badge im-count-badge--prod">
                            {catNode.productCount || 0} products
                          </span>
                          {catNode.pendingCount > 0 && (
                            <span className="im-count-badge im-count-badge--pending">
                              {catNode.pendingCount} pending
                            </span>
                          )}
                          <span className="im-count-badge im-count-badge--seller">
                            {catNode.sellerCount || 0} sellers
                          </span>
                          <span className={`im-status-badge im-status-badge--${catNode.status || 'active'}`}>
                            {catNode.status || 'active'}
                          </span>

                          <button
                            type="button"
                            onClick={(e) => handleOpenActionMenu(e, catNode)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6b7280' }}>
                            <MoreVertIcon fontSize="small" />
                          </button>
                        </div>
                      </div>

                      {/* Subcategories (when expanded) */}
                      {isExpanded && hasChildren && (
                        <div className="im-tree-sub-list">
                          {catNode.children.map(subNode => {
                            const isSubSelected = selectedNodeId === subNode._id;
                            return (
                              <div
                                key={subNode._id}
                                className={`im-tree-sub-row ${isSubSelected ? 'im-tree-sub-row--selected' : ''}`}
                                onClick={() => setSelectedNodeId(subNode._id)}>
                                <span style={{ color: '#9ca3af', fontSize: '14px', marginRight: '4px' }}>├─</span>
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span className="im-tree-sub-name">{subNode.name}</span>
                                  <span className="im-tree-node-slug">/{subNode.slug}</span>
                                </div>

                                <div className="im-tree-node-badges">
                                  <span className="im-count-badge im-count-badge--prod" style={{ fontSize: '11px', padding: '2px 7px' }}>
                                    {subNode.productCount || 0} prods
                                  </span>
                                  {subNode.pendingCount > 0 && (
                                    <span className="im-count-badge im-count-badge--pending" style={{ fontSize: '11px', padding: '2px 7px' }}>
                                      {subNode.pendingCount} pending
                                    </span>
                                  )}
                                  <span className={`im-status-badge im-status-badge--${subNode.status || 'active'}`} style={{ fontSize: '10px' }}>
                                    {subNode.status || 'active'}
                                  </span>

                                  <button
                                    type="button"
                                    onClick={(e) => handleOpenActionMenu(e, subNode)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6b7280' }}>
                                    <MoreVertIcon fontSize="small" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ background: '#ffffff', border: '1px solid #edf0f4', borderRadius: '12px', padding: '48px', textAlign: 'center' }}>
                <CategoryOutlinedIcon style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '12px' }} />
                <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#111315', margin: 0 }}>
                  No categories match your search
                </h3>
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                  Try changing your search term or clearing the status filter.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ═════════════════ TAB 2: SELLER REQUESTS ═════════════════ */}
        {activeTab === 'requests' && (
          <div style={{ background: '#ffffff', border: '1px solid #edf0f4', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #edf0f4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#111315' }}>
                Seller Taxonomy Classification Requests ({requests.length})
              </h3>
              <span style={{ fontSize: '12.5px', color: '#6b7280' }}>
                Review and integrate seller-proposed classifications into the marketplace taxonomy.
              </span>
            </div>

            {requests.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', color: '#4b5563', fontWeight: '700' }}>
                      <th style={{ padding: '12px 18px' }}>Store & Vendor</th>
                      <th style={{ padding: '12px 18px' }}>Requested Classification</th>
                      <th style={{ padding: '12px 18px' }}>Product Example</th>
                      <th style={{ padding: '12px 18px' }}>Status</th>
                      <th style={{ padding: '12px 18px' }}>Date</th>
                      <th style={{ padding: '12px 18px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map(req => (
                      <tr key={req._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 18px' }}>
                          <strong style={{ display: 'block', color: '#111315' }}>
                            {req.store?.name || req.vendor?.storeName || 'Vendor'}
                          </strong>
                          <span style={{ fontSize: '11.5px', color: '#6b7280' }}>
                            {req.vendor?.email || req.vendor?.phone || 'Seller'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 18px' }}>
                          <strong style={{ color: '#8b2626' }}>
                            {req.suggestedCategory}
                          </strong>
                          {req.suggestedSubcategory && (
                            <span style={{ color: '#4b5563' }}> → {req.suggestedSubcategory}</span>
                          )}
                          {req.description && (
                            <p style={{ margin: '2px 0 0', fontSize: '11.5px', color: '#6b7280' }}>
                              "{req.description}"
                            </p>
                          )}
                        </td>
                        <td style={{ padding: '12px 18px', color: '#374151' }}>
                          {req.productName || '—'}
                        </td>
                        <td style={{ padding: '12px 18px' }}>
                          <span className={`im-status-badge im-status-badge--${req.status === 'approved' ? 'active' : req.status === 'rejected' ? 'inactive' : 'archived'}`}>
                            {req.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 18px', color: '#6b7280', fontSize: '12px' }}>
                          {new Date(req.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px 18px', textAlign: 'right' }}>
                          {req.status === 'pending' ? (
                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                              <button
                                type="button"
                                className="im-btn-primary"
                                style={{ padding: '5px 10px', fontSize: '11.5px' }}
                                onClick={() => handleOpenReviewRequest(req, 'approve')}>
                                Approve
                              </button>
                              <button
                                type="button"
                                className="im-btn-secondary"
                                style={{ padding: '5px 10px', fontSize: '11.5px' }}
                                onClick={() => handleOpenReviewRequest(req, 'reject')}>
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>Reviewed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                <CheckCircleOutlineIcon style={{ fontSize: '40px', color: '#16a34a', marginBottom: '8px' }} />
                <h4 style={{ margin: 0, color: '#111315' }}>All Caught Up!</h4>
                <p style={{ margin: '4px 0 0', fontSize: '13px' }}>No pending seller classification requests.</p>
              </div>
            )}
          </div>
        )}

        {/* ═════════════════ 5. RIGHT-SIDE DETAIL DRAWER ═════════════════ */}
        {selectedNodeId && (
          <div className="im-drawer-overlay" onClick={() => setSelectedNodeId(null)}>
            <div className="im-drawer" onClick={(e) => e.stopPropagation()}>
              {drawerLoading ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <Loader />
                </div>
              ) : drawerData?.category ? (
                <>
                  {/* Drawer Header */}
                  <div className="im-drawer-header">
                    <div>
                      <span style={{ fontSize: '11.5px', fontWeight: '700', color: '#8b2626', textTransform: 'uppercase' }}>
                        {drawerData.breadcrumbs}
                      </span>
                      <h2 style={{ fontSize: '18px', fontWeight: '900', color: '#111315', margin: '2px 0 0' }}>
                        {drawerData.category.name}
                      </h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedNodeId(null)}
                      style={{ background: '#f3f4f6', border: 'none', borderRadius: '20px', padding: '6px', cursor: 'pointer' }}>
                      <CloseIcon fontSize="small" />
                    </button>
                  </div>

                  {/* Drawer Body */}
                  <div className="im-drawer-body">
                    {/* Metrics Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                      <div style={{ background: '#f9fafb', padding: '10px 12px', borderRadius: '8px', textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '700' }}>PUBLISHED</span>
                        <strong style={{ display: 'block', fontSize: '18px', color: '#16a34a' }}>
                          {drawerData.metrics.publishedCount}
                        </strong>
                      </div>
                      <div style={{ background: '#f9fafb', padding: '10px 12px', borderRadius: '8px', textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '700' }}>PENDING</span>
                        <strong style={{ display: 'block', fontSize: '18px', color: '#d97706' }}>
                          {drawerData.metrics.pendingCount}
                        </strong>
                      </div>
                      <div style={{ background: '#f9fafb', padding: '10px 12px', borderRadius: '8px', textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '700' }}>SELLERS</span>
                        <strong style={{ display: 'block', fontSize: '18px', color: '#1d4ed8' }}>
                          {drawerData.metrics.sellerCount}
                        </strong>
                      </div>
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '12px', fontWeight: '800', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>
                        Description
                      </h4>
                      <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>
                        {drawerData.category.description || drawerData.category.desc || 'No description provided.'}
                      </p>
                    </div>

                    {/* Subcategories (if primary category) */}
                    {drawerData.subcategories && drawerData.subcategories.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <h4 style={{ fontSize: '12px', fontWeight: '800', color: '#6b7280', textTransform: 'uppercase', margin: 0 }}>
                            Subcategories ({drawerData.subcategories.length})
                          </h4>
                          <button
                            type="button"
                            onClick={() => handleOpenAddSubcategory(drawerData.category)}
                            style={{ background: 'none', border: 'none', color: '#8b2626', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                            + Add Child
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {drawerData.subcategories.map(sub => (
                            <span
                              key={sub._id}
                              onClick={() => setSelectedNodeId(sub._id)}
                              style={{
                                background: '#ffffff',
                                border: '1px solid #edf0f4',
                                borderRadius: '6px',
                                padding: '4px 10px',
                                fontSize: '12px',
                                fontWeight: '700',
                                color: '#374151',
                                cursor: 'pointer'
                              }}>
                              {sub.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Product Activity */}
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '12px', fontWeight: '800', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>
                        Recent Products in Category
                      </h4>
                      {drawerData.recentProducts && drawerData.recentProducts.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {drawerData.recentProducts.map(prod => (
                            <div
                              key={prod._id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                background: '#f9fafb',
                                border: '1px solid #edf0f4',
                                borderRadius: '8px',
                                padding: '8px 12px'
                              }}>
                              <img
                                src={prod.mainImage?.[0]?.imageurl || 'https://via.placeholder.com/40'}
                                alt={prod.name}
                                style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <strong style={{ fontSize: '13px', color: '#111315', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {prod.name}
                                </strong>
                                <span style={{ fontSize: '11.5px', color: '#6b7280' }}>
                                  ₹{prod.price} • {prod.store?.name || 'Local Seller'}
                                </span>
                              </div>
                              <span className={`im-status-badge im-status-badge--${prod.approvalStatus === 'approved' ? 'active' : 'pending'}`}>
                                {prod.approvalStatus || 'approved'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: '12.5px', color: '#9ca3af' }}>No products listed under this category yet.</p>
                      )}
                    </div>

                    {/* Active Sellers */}
                    <div>
                      <h4 style={{ fontSize: '12px', fontWeight: '800', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>
                        Active Sellers Listing Here
                      </h4>
                      {drawerData.sellers && drawerData.sellers.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {drawerData.sellers.map(s => (
                            <div key={s._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffffff', border: '1px solid #edf0f4', borderRadius: '6px', padding: '8px 12px' }}>
                              <div>
                                <strong style={{ fontSize: '13px', color: '#111315' }}>{s.name}</strong>
                                <span style={{ fontSize: '11.5px', color: '#6b7280', display: 'block' }}>{s.phone || s.email}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => history.push(`/store/${s.slug}`)}
                                style={{ background: 'none', border: 'none', color: '#8b2626', cursor: 'pointer' }}>
                                <OpenInNewIcon fontSize="small" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: '12.5px', color: '#9ca3af' }}>No active sellers associated yet.</p>
                      )}
                    </div>
                  </div>

                  {/* Drawer Footer Actions */}
                  <div className="im-drawer-footer">
                    <button
                      type="button"
                      className="im-btn-secondary"
                      style={{ flex: 1 }}
                      onClick={() => handleOpenEdit(drawerData.category)}>
                      <EditOutlinedIcon fontSize="small" />
                      <span>Edit</span>
                    </button>
                    <button
                      type="button"
                      className="im-btn-primary"
                      style={{ flex: 1.5 }}
                      onClick={() => history.push(`/Admin/dashboard/Products?category=${encodeURIComponent(drawerData.category.name)}`)}>
                      <Inventory2OutlinedIcon fontSize="small" />
                      <span>View Products ({drawerData.metrics.totalCount})</span>
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ padding: '30px', textAlign: 'center' }}>
                  <p>Category not found.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═════════════════ 6. NODE CONTEXT ACTIONS MENU ═════════════════ */}
        <Menu
          anchorEl={menuAnchorEl}
          keepMounted
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseActionMenu}>
          <MenuItem onClick={() => handleOpenEdit(menuTargetNode)}>
            <EditOutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
            Edit
          </MenuItem>
          {menuTargetNode?.level === 1 && (
            <MenuItem onClick={() => {
              handleCloseActionMenu();
              handleOpenAddSubcategory(menuTargetNode);
            }}>
              <AddCircleOutlineIcon fontSize="small" style={{ marginRight: '8px' }} />
              Add Subcategory
            </MenuItem>
          )}
          <MenuItem onClick={() => {
            handleCloseActionMenu();
            history.push(`/Admin/dashboard/Products?category=${encodeURIComponent(menuTargetNode?.name)}`);
          }}>
            <Inventory2OutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
            View Products
          </MenuItem>
          <MenuItem onClick={() => handleOpenMerge(menuTargetNode)}>
            <CallMergeOutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
            Merge with Another
          </MenuItem>
          <MenuItem onClick={() => handleToggleStatus(menuTargetNode)}>
            {menuTargetNode?.status === 'active' ? 'Deactivate' : 'Activate'}
          </MenuItem>
          <MenuItem
            onClick={() => handleDeleteNode(menuTargetNode)}
            style={{ color: '#dc2626' }}>
            <DeleteOutlineIcon fontSize="small" style={{ marginRight: '8px' }} />
            Delete / Archive
          </MenuItem>
        </Menu>

        {/* ═════════════════ 7. ADD / EDIT CATEGORY MODAL ═════════════════ */}
        <Dialog
          open={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          maxWidth="sm"
          fullWidth>
          <form onSubmit={handleSaveCategory}>
            <DialogTitle>
              {editingNode ? `Edit Primary Category: "${editingNode.name}"` : 'Create Primary Category'}
            </DialogTitle>
            <DialogContent dividers>
              <TextField
                label="Category Name *"
                fullWidth
                variant="outlined"
                margin="dense"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Footwear, Fashion & Clothing, Home & Living"
              />

              {/* Similarity Warning Box */}
              {similarWarnings.length > 0 && (
                <div className="im-similarity-alert">
                  <span className="im-similarity-title">
                    ⚠ Warning: Similar Category Already Exists
                  </span>
                  <div style={{ fontSize: '12px', color: '#78350f' }}>
                    A category with a similar name already exists. Avoid duplicate creation:
                  </div>
                  <div>
                    {similarWarnings.map(m => (
                      <span key={m.id} className="im-similarity-match-tag">
                        "{m.name}" ({m.level === 1 ? 'Primary' : `Sub under ${m.parentName}`})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <TextField
                label="Slug (optional, auto-generated if blank)"
                fullWidth
                variant="outlined"
                margin="dense"
                value={formSlug}
                onChange={(e) => setFormSlug(e.target.value)}
              />

              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                margin="dense"
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                placeholder="Explain what products belong in this department..."
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value)}
                    label="Status">
                    <MenuItem value="active">Active (Visible in Catalog)</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Display Order"
                  type="number"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={formDisplayOrder}
                  onChange={(e) => setFormDisplayOrder(e.target.value)}
                />
              </div>
            </DialogContent>
            <DialogActions style={{ padding: '16px 24px' }}>
              <Button onClick={() => setIsCategoryModalOpen(false)} color="default">
                Cancel
              </Button>
              <Button type="submit" variant="contained" style={{ background: '#8b2626', color: '#ffffff' }}>
                {editingNode ? 'Save Changes' : 'Create Category'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* ═════════════════ 8. ADD / EDIT SUBCATEGORY MODAL ═════════════════ */}
        <Dialog
          open={isSubcategoryModalOpen}
          onClose={() => setIsSubcategoryModalOpen(false)}
          maxWidth="sm"
          fullWidth>
          <form onSubmit={handleSaveSubcategory}>
            <DialogTitle>
              {editingNode ? `Edit Subcategory: "${editingNode.name}"` : 'Create Subcategory'}
            </DialogTitle>
            <DialogContent dividers>
              <FormControl variant="outlined" margin="dense" fullWidth required>
                <InputLabel>Parent Category *</InputLabel>
                <Select
                  value={formParentId}
                  onChange={(e) => setFormParentId(e.target.value)}
                  label="Parent Category *">
                  {tree.map(c => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Subcategory Name *"
                fullWidth
                variant="outlined"
                margin="dense"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Sneakers, Formal Shoes, Kurtas, Laptops"
              />

              {/* Similarity Warning Box */}
              {similarWarnings.length > 0 && (
                <div className="im-similarity-alert">
                  <span className="im-similarity-title">
                    ⚠ Warning: Similar Subcategory Already Exists
                  </span>
                  <div>
                    {similarWarnings.map(m => (
                      <span key={m.id} className="im-similarity-match-tag">
                        "{m.name}" ({m.parentName ? `under ${m.parentName}` : 'Root'})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                margin="dense"
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
              />

              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value)}
                  label="Status">
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions style={{ padding: '16px 24px' }}>
              <Button onClick={() => setIsSubcategoryModalOpen(false)} color="default">
                Cancel
              </Button>
              <Button type="submit" variant="contained" style={{ background: '#8b2626', color: '#ffffff' }}>
                {editingNode ? 'Save Changes' : 'Create Subcategory'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* ═════════════════ 9. MERGE CATEGORIES MODAL ═════════════════ */}
        <Dialog
          open={isMergeModalOpen}
          onClose={() => setIsMergeModalOpen(false)}
          maxWidth="sm"
          fullWidth>
          <DialogTitle>
            Merge "{mergeSourceNode?.name}" into Another Category
          </DialogTitle>
          <DialogContent dividers>
            <p style={{ fontSize: '13px', color: '#4b5563', margin: '0 0 16px' }}>
              Merging safely reassigns all existing products and subcategories from <strong>"{mergeSourceNode?.name}"</strong> to your chosen target category. Order history and seller associations will be preserved.
            </p>

            <FormControl variant="outlined" fullWidth margin="dense">
              <InputLabel>Target Category *</InputLabel>
              <Select
                value={mergeTargetId}
                onChange={(e) => setMergeTargetId(e.target.value)}
                label="Target Category *">
                {tree
                  .filter(c => c._id !== mergeSourceNode?._id)
                  .map(c => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name} ({c.productCount || 0} products)
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions style={{ padding: '16px 24px' }}>
            <Button onClick={() => setIsMergeModalOpen(false)} color="default">
              Cancel
            </Button>
            <Button
              onClick={handleExecuteMerge}
              variant="contained"
              style={{ background: '#8b2626', color: '#ffffff' }}>
              Confirm & Merge
            </Button>
          </DialogActions>
        </Dialog>

        {/* ═════════════════ 10. REVIEW SELLER REQUEST MODAL ═════════════════ */}
        {reviewingRequest && (
          <Dialog
            open={Boolean(reviewingRequest)}
            onClose={() => setReviewingRequest(null)}
            maxWidth="sm"
            fullWidth>
            <DialogTitle>
              {reviewAction === 'approve' ? 'Approve Seller Classification' : 'Reject Seller Classification'}
            </DialogTitle>
            <DialogContent dividers>
              <div style={{ background: '#f9fafb', padding: '12px 16px', borderRadius: '8px', marginBottom: '14px' }}>
                <strong style={{ fontSize: '13.5px', color: '#111315', display: 'block' }}>
                  Requested: {reviewingRequest.suggestedCategory}
                  {reviewingRequest.suggestedSubcategory && ` → ${reviewingRequest.suggestedSubcategory}`}
                </strong>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  Store: {reviewingRequest.store?.name || reviewingRequest.vendor?.storeName} • Product: {reviewingRequest.productName}
                </span>
              </div>

              {reviewAction === 'approve' && (
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel>Assign Under Category</InputLabel>
                  <Select
                    value={reviewAssignParentId}
                    onChange={(e) => setReviewAssignParentId(e.target.value)}
                    label="Assign Under Category">
                    {tree.map(c => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                label="Admin Feedback / Review Notes"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                margin="dense"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Notes visible to seller or internal audit..."
              />
            </DialogContent>
            <DialogActions style={{ padding: '16px 24px' }}>
              <Button onClick={() => setReviewingRequest(null)} color="default">
                Cancel
              </Button>
              <Button
                onClick={handleExecuteRequestReview}
                variant="contained"
                style={{ background: reviewAction === 'approve' ? '#16a34a' : '#dc2626', color: '#ffffff' }}>
                Confirm {reviewAction === 'approve' ? 'Approval' : 'Rejection'}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </main>
    </div>
  );
}

export default TaxonomyExplorer;
